import { Badge, Card, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { GoogleMap, useJsApiLoader, InfoWindow } from '@react-google-maps/api';
import React, {
    useState,
    useCallback,
    useMemo,
    useRef,
    useEffect,
} from 'react';

/**
 * Google Maps requires inline style for container dimensions.
 * This is the one exception to the no-inline-styles rule.
 */
const MAP_CONTAINER_STYLE = {
    width: '100%',
    height: '400px',
    borderRadius: '0.375rem',
};

const MAP_OPTIONS = {
    disableDefaultUI: true,
    zoomControl: true,
    gestureHandling: 'cooperative',
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: true,
};

/** Colorado-centered default view */
const DEFAULT_CENTER = { lat: 39.0, lng: -105.5 };
const DEFAULT_ZOOM = 7;

const SOURCE_COLOR = '#0d6efd';
const PRODUCT_COLOR = '#198754';

/**
 * Converts a GeoJSON MultiPolygon into a lat/lng bounds object.
 * Used for fitBounds and InfoWindow positioning.
 */
function geojsonToBounds(geojson) {
    if (!geojson || !geojson.coordinates) return null;

    let minLat = 90;
    let maxLat = -90;
    let minLng = 180;
    let maxLng = -180;

    for (const polygon of geojson.coordinates) {
        for (const ring of polygon) {
            for (const [lng, lat] of ring) {
                if (lat < minLat) minLat = lat;
                if (lat > maxLat) maxLat = lat;
                if (lng < minLng) minLng = lng;
                if (lng > maxLng) maxLng = lng;
            }
        }
    }

    return {
        north: maxLat,
        south: minLat,
        east: maxLng,
        west: minLng,
    };
}

/**
 * Converts GeoJSON MultiPolygon coordinates into an array of Google Maps
 * LatLng path arrays suitable for google.maps.Polygon({ paths }).
 *
 * GeoJSON format: [polygon][ring][position] where position = [lng, lat]
 * Google Maps format: [path] where path = [{lat, lng}, ...]
 *
 * The first ring of each polygon is the outer boundary; subsequent rings
 * are holes. Google Maps Polygon handles this natively via the paths array.
 */
function geojsonToPaths(geojson) {
    if (!geojson || !geojson.coordinates) return null;

    const paths = [];
    for (const polygon of geojson.coordinates) {
        for (const ring of polygon) {
            paths.push(ring.map(([lng, lat]) => ({ lat, lng })));
        }
    }
    return paths.length > 0 ? paths : null;
}

/**
 * Interactive map for the Data Discovery page. Shows data source and product
 * coverage areas as colored rectangle overlays. Click an overlay to see details
 * and a link to the detail page.
 *
 * Uses imperative Google Maps API calls to create Rectangle overlays, which
 * is more reliable than the declarative React wrapper components.
 *
 * When `spatialFilterActive` is true, a dashed boundary rectangle shows the
 * current viewport and `onBoundsChange` fires with the map bounds whenever
 * the user finishes panning/zooming.
 */
export default function DiscoveryMap({
    sources,
    products,
    highlightedItemId,
    highlightedItemType,
    spatialFilterActive,
    onBoundsChange,
}) {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    const [selectedItem, setSelectedItem] = useState(null);
    const [showSources, setShowSources] = useState(true);
    const [showProducts, setShowProducts] = useState(true);
    const [map, setMap] = useState(null);
    const overlaysRef = useRef([]);
    const overlayListenersRef = useRef([]);

    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: apiKey || '',
    });

    const sourceOverlays = useMemo(
        () =>
            (sources || [])
                .filter((s) => s.geospatial_bound)
                .map((s) => ({
                    ...s,
                    bounds: geojsonToBounds(s.geospatial_bound),
                    paths: geojsonToPaths(s.geospatial_bound),
                    itemType: 'source',
                }))
                .filter((s) => s.bounds),
        [sources]
    );

    const productOverlays = useMemo(
        () =>
            (products || [])
                .filter((p) => p.geospatial_bound)
                .map((p) => ({
                    ...p,
                    bounds: geojsonToBounds(p.geospatial_bound),
                    paths: geojsonToPaths(p.geospatial_bound),
                    itemType: 'product',
                }))
                .filter((p) => p.bounds),
        [products]
    );

    const allOverlays = useMemo(
        () => [
            ...(showSources ? sourceOverlays : []),
            ...(showProducts ? productOverlays : []),
        ],
        [sourceOverlays, productOverlays, showSources, showProducts]
    );

    const onMapLoad = useCallback((mapInstance) => {
        setMap(mapInstance);
    }, []);

    /** Fire onBoundsChange with the current viewport when the map settles. */
    const onMapIdle = useCallback(() => {
        if (!spatialFilterActive || !map || !onBoundsChange) return;
        const b = map.getBounds();
        if (!b) return;
        const ne = b.getNorthEast();
        const sw = b.getSouthWest();
        onBoundsChange({
            west: sw.lng(),
            south: sw.lat(),
            east: ne.lng(),
            north: ne.lat(),
        });
    }, [map, spatialFilterActive, onBoundsChange]);

    /**
     * Imperatively create Google Maps Polygon overlays whenever
     * the map instance or overlay data changes. Uses actual GeoJSON
     * polygon shapes when available, falling back to Rectangle for
     * items with only bounding-box data. Uses state (not ref) for
     * the map instance so this effect re-runs when the map loads.
     */
    useEffect(() => {
        if (!map || !window.google) return;

        // Clear previous overlays and their listeners
        overlayListenersRef.current.forEach((l) =>
            window.google.maps.event.removeListener(l)
        );
        overlayListenersRef.current = [];
        overlaysRef.current.forEach((r) => r.setMap(null));
        overlaysRef.current = [];

        let highlightedBounds = null;

        allOverlays.forEach((item) => {
            const color =
                item.itemType === 'source' ? SOURCE_COLOR : PRODUCT_COLOR;
            const isHighlighted =
                highlightedItemId != null &&
                item.id === highlightedItemId &&
                item.itemType === highlightedItemType;
            const isHoverActive = highlightedItemId != null;

            // When a card is hovered, hide all other overlays
            if (isHoverActive && !isHighlighted) return;

            if (isHighlighted) {
                highlightedBounds = item.bounds;
            }

            let overlay;
            if (item.paths) {
                // Render actual polygon shape from GeoJSON coordinates
                overlay = new window.google.maps.Polygon({
                    paths: item.paths,
                    map,
                    fillColor: color,
                    fillOpacity: isHighlighted ? 0.15 : 0.05,
                    strokeColor: color,
                    strokeOpacity: isHighlighted ? 1 : 0.5,
                    strokeWeight: isHighlighted ? 3 : 1.5,
                    clickable: true,
                });
            } else {
                // Fallback to rectangle for items without polygon data
                overlay = new window.google.maps.Rectangle({
                    bounds: item.bounds,
                    map,
                    fillColor: color,
                    fillOpacity: isHighlighted ? 0.15 : 0,
                    strokeColor: color,
                    strokeOpacity: isHighlighted ? 1 : 0.5,
                    strokeWeight: isHighlighted ? 3 : 1.5,
                    clickable: true,
                });
            }

            const listener = overlay.addListener('click', () => {
                setSelectedItem(item);
                map.fitBounds(item.bounds, {
                    top: 20,
                    bottom: 20,
                    left: 20,
                    right: 20,
                });
            });
            overlayListenersRef.current.push(listener);

            overlaysRef.current.push(overlay);
        });

        // Zoom to highlighted item's bounds, or reset to default view
        if (highlightedBounds) {
            map.fitBounds(highlightedBounds, {
                top: 20,
                bottom: 20,
                left: 20,
                right: 20,
            });
        } else {
            map.setCenter(DEFAULT_CENTER);
            map.setZoom(DEFAULT_ZOOM);
        }

        // Cleanup on unmount or data change — remove listeners and overlays
        return () => {
            overlayListenersRef.current.forEach((l) =>
                window.google.maps.event.removeListener(l)
            );
            overlayListenersRef.current = [];
            overlaysRef.current.forEach((r) => r.setMap(null));
            overlaysRef.current = [];
        };
    }, [map, allOverlays, highlightedItemId, highlightedItemType]);

    /** Show a dashed viewport-edge rectangle when spatial filter is active. */
    const viewportRectRef = useRef(null);
    useEffect(() => {
        if (!map || !window.google) return;

        // Clean up previous viewport rect
        if (viewportRectRef.current) {
            viewportRectRef.current.setMap(null);
            viewportRectRef.current = null;
        }

        if (!spatialFilterActive) return;

        const b = map.getBounds();
        if (!b) return;

        // Inset the rectangle slightly so it's visible within the viewport
        const ne = b.getNorthEast();
        const sw = b.getSouthWest();
        const latPad = (ne.lat() - sw.lat()) * 0.02;
        const lngPad = (ne.lng() - sw.lng()) * 0.02;

        viewportRectRef.current = new window.google.maps.Rectangle({
            bounds: {
                north: ne.lat() - latPad,
                south: sw.lat() + latPad,
                east: ne.lng() - lngPad,
                west: sw.lng() + lngPad,
            },
            map,
            fillColor: '#6c757d',
            fillOpacity: 0.03,
            strokeColor: '#6c757d',
            strokeOpacity: 0.6,
            strokeWeight: 2,
            clickable: false,
            zIndex: -1,
        });

        // Redraw on bounds change
        const listener = map.addListener('idle', () => {
            if (!viewportRectRef.current) return;
            const nb = map.getBounds();
            if (!nb) return;
            const nne = nb.getNorthEast();
            const nsw = nb.getSouthWest();
            const nlp = (nne.lat() - nsw.lat()) * 0.02;
            const ngp = (nne.lng() - nsw.lng()) * 0.02;
            viewportRectRef.current.setBounds({
                north: nne.lat() - nlp,
                south: nsw.lat() + nlp,
                east: nne.lng() - ngp,
                west: nsw.lng() + ngp,
            });
        });

        return () => {
            window.google.maps.event.removeListener(listener);
            if (viewportRectRef.current) {
                viewportRectRef.current.setMap(null);
                viewportRectRef.current = null;
            }
        };
    }, [map, spatialFilterActive]);

    if (!apiKey) {
        return (
            <Card
                className="text-center bg-light mb-3"
                role="img"
                aria-label="Map placeholder — configure Google Maps API key to enable"
            >
                <Card.Body className="py-4">
                    <span className="fs-2 d-block mb-2" aria-hidden="true">
                        🗺️
                    </span>
                    <span className="text-muted small">
                        Configure Google Maps API key to view coverage map
                    </span>
                </Card.Body>
            </Card>
        );
    }

    if (loadError) {
        return (
            <Card
                className="text-center bg-light mb-3"
                role="img"
                aria-label="Map unavailable — the map failed to load"
            >
                <Card.Body className="py-4">
                    <span className="fs-2 d-block mb-2" aria-hidden="true">
                        🗺️
                    </span>
                    <span className="text-muted small">
                        Map unavailable right now
                    </span>
                </Card.Body>
            </Card>
        );
    }

    if (!isLoaded) {
        return (
            <div
                className="d-flex align-items-center justify-content-center bg-light rounded mb-3"
                role="status"
            >
                <Spinner
                    animation="border"
                    variant="primary"
                    size="sm"
                    className="my-5"
                />
                <span className="visually-hidden">Loading map...</span>
            </div>
        );
    }

    return (
        <div className="mb-3">
            <div className="d-flex align-items-center gap-3 mb-2">
                <small className="text-muted">
                    Coverage map ({allOverlays.length} items)
                </small>
                <div className="d-flex gap-2">
                    {sourceOverlays.length > 0 && (
                        <Badge
                            role="button"
                            tabIndex={0}
                            className="fw-normal"
                            style={{
                                backgroundColor: showSources
                                    ? SOURCE_COLOR
                                    : '#adb5bd',
                                cursor: 'pointer',
                            }}
                            onClick={() => setShowSources((v) => !v)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    setShowSources((v) => !v);
                                }
                            }}
                            aria-pressed={showSources}
                            aria-label={`Toggle sources (${sourceOverlays.length})`}
                        >
                            Sources ({sourceOverlays.length})
                        </Badge>
                    )}
                    {productOverlays.length > 0 && (
                        <Badge
                            role="button"
                            tabIndex={0}
                            className="fw-normal"
                            style={{
                                backgroundColor: showProducts
                                    ? PRODUCT_COLOR
                                    : '#adb5bd',
                                cursor: 'pointer',
                            }}
                            onClick={() => setShowProducts((v) => !v)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    setShowProducts((v) => !v);
                                }
                            }}
                            aria-pressed={showProducts}
                            aria-label={`Toggle products (${productOverlays.length})`}
                        >
                            Products ({productOverlays.length})
                        </Badge>
                    )}
                </div>
                {spatialFilterActive && (
                    <small className="text-success fw-semibold ms-auto">
                        Filtering by visible area
                    </small>
                )}
            </div>
            <GoogleMap
                mapContainerStyle={MAP_CONTAINER_STYLE}
                center={DEFAULT_CENTER}
                zoom={DEFAULT_ZOOM}
                options={MAP_OPTIONS}
                onLoad={onMapLoad}
                onIdle={onMapIdle}
                onClick={() => {
                    setSelectedItem(null);
                    if (map && !highlightedItemId) {
                        map.setCenter(DEFAULT_CENTER);
                        map.setZoom(DEFAULT_ZOOM);
                    }
                }}
            >
                {selectedItem && (
                    <InfoWindow
                        position={{
                            lat:
                                (selectedItem.bounds.north +
                                    selectedItem.bounds.south) /
                                2,
                            lng:
                                (selectedItem.bounds.east +
                                    selectedItem.bounds.west) /
                                2,
                        }}
                        onCloseClick={() => {
                            setSelectedItem(null);
                            if (!highlightedItemId) {
                                map.setCenter(DEFAULT_CENTER);
                                map.setZoom(DEFAULT_ZOOM);
                            }
                        }}
                    >
                        <div className="p-1">
                            <strong>{selectedItem.name}</strong>
                            <br />
                            <small className="text-muted">
                                {selectedItem.itemType === 'source'
                                    ? 'Data Source'
                                    : 'Data Product'}
                            </small>
                            <br />
                            <Link
                                to={`/data/${selectedItem.itemType === 'source' ? 'sources' : 'products'}/${selectedItem.id}`}
                                className="small"
                            >
                                View details &rarr;
                            </Link>
                        </div>
                    </InfoWindow>
                )}
            </GoogleMap>
        </div>
    );
}

DiscoveryMap.propTypes = {
    sources: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number })),
    products: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number })),
    /** ID of the item to highlight on the map (from card hover) */
    highlightedItemId: PropTypes.number,
    /** Type of the highlighted item: 'source' or 'product' */
    highlightedItemType: PropTypes.oneOf(['source', 'product']),
    /** Whether the spatial filter is active (filter results by map viewport) */
    spatialFilterActive: PropTypes.bool,
    /** Callback fired with {west, south, east, north} when map viewport settles */
    onBoundsChange: PropTypes.func,
};
